// File: src/lib/graphql/index.ts
// Purpose: Central export for all GraphQL APIs
// VIBE - GraphQL API Hub

// Import all API classes first
import { AuthAPI } from './queries/auth'
import { OrganizationAPI } from './queries/organization'
import { EmployeeAPI } from './queries/employee'
import { ProjectAPI } from './queries/project'
import { TaskAPI } from './queries/task'
import { TimesheetAPI } from './queries/timesheet'
import { LLMAPI } from './queries/llm'
import * as SprintAPI from './queries/sprint'

// Import client utilities
import { 
  graphqlClient, 
  setAuthToken, 
  clearAuthToken, 
  getAuthToken,
  executeGraphQL,
  SubscriptionClient,
  subscriptionClient
} from './client'

// Re-export client utilities
export { 
  graphqlClient, 
  setAuthToken, 
  clearAuthToken, 
  getAuthToken,
  executeGraphQL,
  SubscriptionClient,
  subscriptionClient
}

// Auth API
export { AuthAPI } from './queries/auth'
export type { User, Employee, AuthPayload, RegisterInput, LoginInput } from './queries/auth'

// Organization API
export { OrganizationAPI } from './queries/organization'
export type { 
  Organization, 
  OrganizationType, 
  Department, 
  Designation 
} from './queries/organization'

// Employee API
export { EmployeeAPI } from './queries/employee'
export type {
  EmployeeDetails,
  AzureAuthConfig,
  CalculateEmployeeHourInput,
  EmployeeHourCalculation,
  EmployeeHourByMonth
} from './queries/employee'

// Project API
export { ProjectAPI } from './queries/project'
export type { 
  Project,
  ProjectType,
  ProjectStatus,
  TeamMember,
  Milestone,
  Task
} from './queries/project'

// Task API
export { TaskAPI } from './queries/task'

// Timesheet API
export { TimesheetAPI } from './queries/timesheet'

// LLM API
export { LLMAPI } from './queries/llm'

// Sprint API
export * from './queries/sprint'

// API Configuration
export const API_CONFIG = {
  endpoint: import.meta.env.VITE_GRAPHQL_ENDPOINT || 'https://platform.instadev.ai/graphql',
  wsEndpoint: import.meta.env.VITE_WS_ENDPOINT || 'wss://platform.instadev.ai/graphql',
  apiVersion: '1.0.0',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000
}

// Initialize auth token from storage
if (typeof window !== 'undefined') {
  const storedToken = localStorage.getItem('authToken')
  if (storedToken) {
    setAuthToken(storedToken)
  }
}

// Export a unified API object for convenience
export const InstaDev = {
  Auth: AuthAPI,
  Organization: OrganizationAPI,
  Employee: EmployeeAPI,
  Project: ProjectAPI,
  Task: TaskAPI,
  Timesheet: TimesheetAPI,
  LLM: LLMAPI,
  Sprint: SprintAPI,
  config: API_CONFIG,
  client: {
    setToken: setAuthToken,
    clearToken: clearAuthToken,
    getToken: getAuthToken
  }
}